export type WorkbookAccessLevel = "Author" | "Read Write" | "Read Only";

export interface WorkbookSnapshotData {
	propertyName: string;
	workbookName: string;
	status: string;
	author: string;
	accessLevel: WorkbookAccessLevel;
	description: string;
}

export interface WorkbookSectionsData {
	hotelDescription: string;
	isp: {
		metadata: {
			provider: string;
			circuitId: string;
			accountNumber: string;
			contact: string;
		};
		general: {
			sla: string;
			handoffType: string;
			escalation: string;
		};
		network: {
			wanCidr: string;
			gateway: string;
			primaryDns: string;
			secondaryDns: string;
		};
		router: {
			model: string;
			lanHandoff: string;
			wanInterface: string;
			notes: string;
		};
	};
	equipmentList: WorkbookEquipmentRowData[];
	switchPortMap: WorkbookSwitchPortRowData[];
	vlanLegend: WorkbookVlanLegendRowData[];
	equipmentNotes: string;
	switchPortMapNotes: string;
	vlanLegendNotes: string;
}

export interface WorkbookEquipmentRowData {
	[key: string]: string;
	index: string;
	type: string;
	category: string;
	vendor: string;
	model: string;
	ipAddress: string;
	lanMacAddress: string;
	serialNumber: string;
	name: string;
	location: string;
	deviceMode: string;
	notes: string;
}

export interface WorkbookSwitchPortRowData {
	[key: string]: string;
	switchName: string;
	port: string;
	linkLocation: string;
	nativeVlan: string;
	allowedVlans: string;
	description: string;
}

export interface WorkbookVlanLegendRowData {
	[key: string]: string;
	section: string;
	vlanId: string;
	vlanName: string;
	subnet: string;
	defaultGateway: string;
}

export interface WorkbookSnapshot {
	id: string;
	createdAt: string;
	createdBy: string;
	reason: string;
	data: WorkbookSnapshotData;
	sections: WorkbookSectionsData;
}

export interface WorkbookRecord {
	id: string;
	activeSnapshotId: string;
	snapshots: WorkbookSnapshot[];
}

export interface WorkbookSummary extends WorkbookSnapshotData {
	id: string;
	updatedAt: string;
	versionCount: number;
	activeSnapshotId: string;
}

function withSnapshot(
	id: string,
	createdAt: string,
	createdBy: string,
	reason: string,
	data: WorkbookSnapshotData,
	sections: WorkbookSectionsData,
): WorkbookSnapshot {
	return {
		id,
		createdAt,
		createdBy,
		reason,
		data,
		sections,
	};
}

export function defaultWorkbookSections(): WorkbookSectionsData {
	return {
		hotelDescription: "",
		isp: {
			metadata: {
				provider: "",
				circuitId: "",
				accountNumber: "",
				contact: "",
			},
			general: {
				sla: "",
				handoffType: "",
				escalation: "",
			},
			network: {
				wanCidr: "",
				gateway: "",
				primaryDns: "",
				secondaryDns: "",
			},
			router: {
				model: "",
				lanHandoff: "",
				wanInterface: "",
				notes: "",
			},
		},
		equipmentList: [],
		switchPortMap: [],
		vlanLegend: [],
		equipmentNotes: "",
		switchPortMapNotes: "",
		vlanLegendNotes: "",
	};
}

export const MOCK_WORKBOOK_RECORDS: WorkbookRecord[] = [
	{
		id: "biscayne-main-renovation",
		activeSnapshotId: "snap-biscayne-main-2",
		snapshots: [
			withSnapshot(
				"snap-biscayne-main-1",
				"2026-05-20T10:15:00Z",
				"Monica Reyes",
				"Initial workbook import",
				{
					propertyName: "Biscayne Resort",
					workbookName: "Main Renovation 2026",
					status: "Draft",
					author: "Monica Reyes",
					accessLevel: "Author",
					description:
						"Primary property workbook covering hotel description, ISP details, and switching cutover.",
				},
				{
					...defaultWorkbookSections(),
					hotelDescription:
						"250-room beachfront property with conference center and high-density guest Wi-Fi demand.",
					isp: {
						metadata: {
							provider: "GulfNet Fiber",
							circuitId: "GF-884213",
							accountNumber: "",
							contact: "noc@gulfnet.example",
						},
						general: {
							sla: "4-hour response",
							handoffType: "Dual handoff in MDF",
							escalation: "NOC tier-2",
						},
						network: {
							wanCidr: "/29",
							gateway: "HA pair",
							primaryDns: "",
							secondaryDns: "",
						},
						router: {
							model: "ISR-4451",
							lanHandoff: "Gi0/0/1",
							wanInterface: "",
							notes: "Edge router in MDF",
						},
					},
					equipmentList: [
						{
							index: "1",
							type: "Ethernet",
							category: "Switch",
							vendor: "Cisco",
							model: "C9200",
							ipAddress: "10.120.0.10",
							lanMacAddress: "00:11:22:33:44:55",
							serialNumber: "FDO12345",
							name: "IDF-SW1",
							location: "IDF-A",
							deviceMode: "Trunk",
							notes: "Guest AP uplinks",
						},
					],
					switchPortMap: [
						{
							switchName: "IDF-SW1",
							port: "Gi1/0/1",
							linkLocation: "Room 101",
							nativeVlan: "120",
							allowedVlans: "120,210",
							description: "Guest AP",
						},
					],
					vlanLegend: [
						{
							section: "Guest",
							vlanId: "120",
							vlanName: "Guest VLAN",
							subnet: "10.120.0.0/24",
							defaultGateway: "10.120.0.1",
						},
					],
					equipmentNotes:
						"Switches and AP inventory imported from installer handoff package.",
					switchPortMapNotes:
						"Port map validated for IDF-A and IDF-B floors 1-8.",
					vlanLegendNotes:
						"Guest VLAN 120, Staff VLAN 30, Voice VLAN 210 confirmed with local IT.",
				},
			),
			withSnapshot(
				"snap-biscayne-main-2",
				"2026-05-23T14:20:00Z",
				"Monica Reyes",
				"Published active baseline",
				{
					propertyName: "Biscayne Resort",
					workbookName: "Main Renovation 2026",
					status: "Active",
					author: "Monica Reyes",
					accessLevel: "Author",
					description:
						"Primary property workbook covering hotel description, ISP details, and switching cutover.",
				},
				{
					...defaultWorkbookSections(),
					hotelDescription:
						"250-room beachfront property with conference center and high-density guest Wi-Fi demand.",
					isp: {
						metadata: {
							provider: "GulfNet Fiber",
							circuitId: "GF-884213",
							accountNumber: "",
							contact: "noc@gulfnet.example",
						},
						general: {
							sla: "4-hour response",
							handoffType: "Dual handoff in MDF",
							escalation: "NOC tier-2",
						},
						network: {
							wanCidr: "/29",
							gateway: "HA pair",
							primaryDns: "",
							secondaryDns: "",
						},
						router: {
							model: "ISR-4451",
							lanHandoff: "Gi0/0/1",
							wanInterface: "",
							notes: "Edge router in MDF",
						},
					},
					equipmentList: [
						{
							index: "1",
							type: "Ethernet",
							category: "Switch",
							vendor: "Cisco",
							model: "C9200",
							ipAddress: "10.120.0.10",
							lanMacAddress: "00:11:22:33:44:55",
							serialNumber: "FDO12345",
							name: "IDF-SW1",
							location: "IDF-A",
							deviceMode: "Trunk",
							notes: "Guest AP uplinks",
						},
					],
					switchPortMap: [
						{
							switchName: "IDF-SW1",
							port: "Gi1/0/1",
							linkLocation: "Room 101",
							nativeVlan: "120",
							allowedVlans: "120,210",
							description: "Guest AP",
						},
					],
					vlanLegend: [
						{
							section: "Guest",
							vlanId: "120",
							vlanName: "Guest VLAN",
							subnet: "10.120.0.0/24",
							defaultGateway: "10.120.0.1",
						},
					],
					equipmentNotes:
						"Equipment inventory reconciled with Meraki import on 2026-05-23.",
					switchPortMapNotes:
						"Switch maps normalized with naming convention SW-IDF-<floor>-<rack>.",
					vlanLegendNotes:
						"Added VLAN 340 for conference AV isolation with ACL restrictions.",
				},
			),
		],
	},
	{
		id: "biscayne-tower-refresh",
		activeSnapshotId: "snap-biscayne-tower-1",
		snapshots: [
			withSnapshot(
				"snap-biscayne-tower-1",
				"2026-05-21T09:45:00Z",
				"Elliot Ford",
				"Created refresh workbook",
				{
					propertyName: "Biscayne Resort",
					workbookName: "Tower Refresh",
					status: "Draft",
					author: "Elliot Ford",
					accessLevel: "Read Write",
					description:
						"In-progress refresh for guest room access layer and AP placement imports.",
				},
				{
					...defaultWorkbookSections(),
					hotelDescription:
						"Tower wing modernization with staged outage windows by floor.",
				},
			),
		],
	},
	{
		id: "harbor-inn-wireless-upgrade",
		activeSnapshotId: "snap-harbor-wireless-1",
		snapshots: [
			withSnapshot(
				"snap-harbor-wireless-1",
				"2026-05-19T17:10:00Z",
				"Priya Patel",
				"Imported from handoff package",
				{
					propertyName: "Harbor Inn",
					workbookName: "Wireless Upgrade",
					status: "Review",
					author: "Priya Patel",
					accessLevel: "Read Only",
					description:
						"Legacy workbook imported from handoff package and pending final author review.",
				},
				{
					...defaultWorkbookSections(),
					isp: {
						...defaultWorkbookSections().isp,
						metadata: {
							...defaultWorkbookSections().isp.metadata,
							provider: "Pending ISP handoff metadata normalization",
						},
					},
				},
			),
		],
	},
];

const FALLBACK_SUMMARY: WorkbookSummary = {
	id: "new-workbook",
	propertyName: "New Property",
	workbookName: "New Workbook",
	status: "Draft",
	author: "Current User",
	updatedAt: "2026-05-25 09:00",
	accessLevel: "Author",
	description: "New mock workbook created from the React parity flow.",
	versionCount: 1,
	activeSnapshotId: "snapshot-new",
};

function activeSnapshot(record: WorkbookRecord): WorkbookSnapshot {
	return (
		record.snapshots.find(
			(snapshot) => snapshot.id === record.activeSnapshotId,
		) ?? record.snapshots[record.snapshots.length - 1]!
	);
}

function formatTimestamp(isoTimestamp: string): string {
	return isoTimestamp.slice(0, 16).replace("T", " ");
}

export function toWorkbookSummary(record: WorkbookRecord): WorkbookSummary {
	const snapshot = activeSnapshot(record);
	return {
		id: record.id,
		...snapshot.data,
		updatedAt: formatTimestamp(snapshot.createdAt),
		versionCount: record.snapshots.length,
		activeSnapshotId: snapshot.id,
	};
}

export function listWorkbookSummaries(
	records: WorkbookRecord[],
): WorkbookSummary[] {
	return records.map(toWorkbookSummary);
}

export const MOCK_WORKBOOKS: WorkbookSummary[] = listWorkbookSummaries(
	MOCK_WORKBOOK_RECORDS,
);

export function filterWorkbooks(
	workbooks: WorkbookSummary[],
	query: string,
): WorkbookSummary[] {
	const normalizedQuery = query.trim().toLowerCase();
	if (!normalizedQuery) {
		return workbooks;
	}

	return workbooks.filter((workbook) => {
		const haystack = [
			workbook.propertyName,
			workbook.workbookName,
			workbook.status,
			workbook.author,
			workbook.accessLevel,
			workbook.description,
		]
			.join(" ")
			.toLowerCase();

		return haystack.includes(normalizedQuery);
	});
}

export function getWorkbookById(id: string): WorkbookSummary {
	return MOCK_WORKBOOKS.find((workbook) => workbook.id === id) ?? {
		...FALLBACK_SUMMARY,
		id,
	};
}

export function getWorkbookRecordById(id: string): WorkbookRecord | null {
	return MOCK_WORKBOOK_RECORDS.find((record) => record.id === id) ?? null;
}
